import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Stack from '@mui/material/Stack';
import CssBaseline from '@mui/material/CssBaseline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import GenerateQR from './GenerateQR';
import AppShellNav from '../shell/AppShellNav';
import AppShellToolbar from '../shell/AppShellToolbar';
import { getFreeAppTheme } from '../shell/getFreeAppTheme';
import { exportLoanPlanExcel } from '../utils/loanCalculatorExport';
import { calculateLoanConditions } from '../utils/loanCalculation';
import '../shell/appShell.css';
import '../shell/freeAppContent.css';

const formatAmount = (number, currency) => {
    const amount = number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    const symbol = currency.trim();
    return symbol ? `${symbol} ${amount}` : amount;
};

export default function LoanCalculator() {
  // 1. State for form inputs and results
  const [annualRate, setAnnualRate] = useState(''); // Tasa de interés anual (%)
  const [numPeriods, setNumPeriods] = useState(''); // Número de períodos (meses)
  const [loanAmount, setLoanAmount] = useState(''); // Monto del préstamo
  const [amortizations, setAmortizations] = useState(''); // Amortizaciones a capital (opcional)
  const [currency, setCurrency] = useState(''); // Símbolo de moneda (opcional)
  const formatCurrency = (number) => formatAmount(number, currency);

  const [monthlyPayment, setMonthlyPayment] = useState(null);
  const [totalInterest, setTotalInterest] = useState(null);
  const [totalPaid, setTotalPaid] = useState(null);
  const [paymentPlan, setPaymentPlan] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const theme = useMemo(() => getFreeAppTheme(), []);

  // 2. Handle calculation logic
  const handleCalculate = (event) => {
    event.preventDefault();
    setError('');
    setMonthlyPayment(null);
    setPaymentPlan([]);
    setTotalInterest(null);
    setTotalPaid(null);

    try {
      const extraPayments = amortizations.trim() ? amortizations.split(',').map(value => Number(value.trim() || 0)) : [];
      const result = calculateLoanConditions({ principal: Number(loanAmount), annualRate: Number(annualRate), months: Number(numPeriods), extraPayments });
      setMonthlyPayment(result.summary.monthlyPayment);
      setTotalInterest(result.summary.totalInterest);
      setTotalPaid(result.summary.totalPaid);
      setPaymentPlan(result.paymentPlan);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExportExcel = () => {
    if (!paymentPlan.length) return;
    exportLoanPlanExcel({
      loanAmount,
      annualRate,
      numPeriods,
      amortizations,
      monthlyPayment,
      totalInterest,
      totalPaid,
      currency,
      paymentPlan,
    });
  };

  // La MISMA función de navegación inteligente
  const handleSmartBack = (fallbackRoute) => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate(fallbackRoute, { replace: true });
    }
  };
  // 3. Render the Component
  return (
    <div className="app-shell free-app-shell">
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Helmet>
          <title>Calculadora de Préstamos - DevsSoft</title>
          <meta name="description" content="Calcula la amortización de tu préstamo." />
          <meta name="keywords" content="calculadora prestamos, amortizacion, finanzas personales" />
        </Helmet>
        <AppShellNav />
        <main className="app-shell-main">
          <div className="apps-wrap">
            <AppShellToolbar onBack={() => handleSmartBack('/apps')} />
            <Box className="free-app-content">
              <Container id="loan-calculator" sx={{ py: { xs: 1, sm: 3 }, px: 0 }}>
        <Typography component="h2" variant="h3" color="text.primary" align="center" sx={{ mb: 4 }}>
          Calculadora de Préstamos 💰
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          
          {/* --- Calculator Form Card --- */}
          <Grid item xs={12} sm={8} md={5}>
            <Card variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Ingresa los Datos del Préstamo
              </Typography>
              <Box component="form" onSubmit={handleCalculate} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Moneda (opcional)"
                  type="text"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  fullWidth
                  placeholder="Ej: $, Bs, USD, €"
                  inputProps={{ maxLength: 6 }}
                />
                <TextField
                  label={currency.trim() ? `Monto del préstamo (${currency.trim()})` : 'Monto del préstamo'}
                  type="number"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                  required
                  fullWidth
                  inputProps={{ step: "0.01", min: "0.01" }}
                />
                <TextField
                  label="Tasa de interés anual (%)"
                  type="number"
                  value={annualRate}
                  onChange={(e) => setAnnualRate(e.target.value)}
                  required
                  fullWidth
                  inputProps={{ step: "0.01", min: "0" }}
                />
                <TextField
                  label="Número de períodos (meses)"
                  type="number"
                  value={numPeriods}
                  onChange={(e) => setNumPeriods(e.target.value)}
                  required
                  fullWidth
                  inputProps={{ step: "1", min: "1", max: "600" }}
                />
                <TextField
                  label="Amortizaciones extra (opcional, separadas por coma)"
                  type="text"
                  value={amortizations}
                  onChange={(e) => setAmortizations(e.target.value)}
                  fullWidth
                  placeholder="Ej: 100,0,300,0,400"
                />
                <Button type="submit" variant="contained" color="primary" size="large" sx={{ mt: 2 }}>
                  Calcular
                </Button>
                {error && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {error}
                  </Typography>
                )}
              </Box>
            </Card>
          </Grid>
          
          {/* --- Results Card --- */}
          <Grid item xs={12} sm={8} md={5}>
            <Card variant="outlined" sx={{ p: 3, boxShadow: 3, minHeight: '100%' }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Resultados del Préstamo
              </Typography>
              <Box sx={{ mt: 2 }}>
                {monthlyPayment !== null ? (
                  <>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Pago Mensual Base (Cuota): {formatCurrency(monthlyPayment)}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Total de Intereses Pagados: {formatCurrency(totalInterest)}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Total Pagado (Capital + Intereses): {formatCurrency(totalPaid)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      *El pago mensual total real puede variar si hay amortizaciones extra en un mes.*
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadOutlinedIcon />}
                      onClick={handleExportExcel}
                    >
                      Exportar Excel
                    </Button>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Ingresa los datos y haz clic en 'Calcular' para ver los resultados.
                    <GenerateQR 
                      title="Devssoft - Calcula Créditos" 
                      description={"Ingresa a la herramienta con este QR"}
                    />
                  </Typography>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
        
        {/* --- Amortization Table --- */}
        {paymentPlan.length > 0 && (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              sx={{ mt: 8, mb: 3 }}
            >
              <Typography component="h3" variant="h5" align="center" sx={{ flex: 1 }}>
                Plan de Pagos con Amortizaciones
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={handleExportExcel}
              >
                Exportar Excel
              </Button>
            </Stack>
            <TableContainer component={Card} variant="outlined" sx={{ overflowX: 'auto', boxShadow: 3 }}>
              <Table size="small" aria-label="loan amortization table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>Mes</TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }} align="right">Saldo Inicial</TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }} align="right">Interés</TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }} align="right">Pago Capital</TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }} align="right">Amort. Extra</TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }} align="right">Pago Total</TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }} align="right">Saldo Final</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentPlan.map((row, index) => (
                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">{row.month}</TableCell>
                      <TableCell align="right">{formatCurrency(row.initialBalance)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.interest)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.principalPayment)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.extraAmortization)}</TableCell>
                      <TableCell align="right">
                          {formatCurrency(row.totalPayment)}
                      </TableCell>
                      <TableCell align="right">
                          {formatCurrency(row.finalBalance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Container>
            </Box>
          </div>
        </main>
      </ThemeProvider>
    </div>
  );
}